import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  LabelList,
} from 'recharts';
import { Header } from '../components/Header';
import { RoadmapSection } from '../components/RoadmapSection';
import {
  RoadmapEditModal,
  type RoadmapEditTarget,
} from '../components/RoadmapEditModal';
import { OutputModal, type ExportOptions } from '../components/OutputModal';
import { api } from '../lib/api';
import { useFactors } from '../lib/factors';
import { useDiagnosticRoadmap } from '../lib/roadmap';
import { exportReportToPdf } from '../lib/pdfExport';
import { exportReportToPptx } from '../lib/pptxExport';
import { summarize, type FactorScoreInput } from '../lib/scoring';
import type { Client, Diagnostic, FactorScore } from '@shared/models';
import type { Factor } from '@shared/factors';
import type { RoadmapItem } from '@shared/roadmap';

interface DiagnosticDetail {
  diagnostic: Diagnostic;
  client: Client;
  scores: FactorScore[];
}

const VERDICT_COLOR = {
  A: 'bg-rank-5 text-white',
  B: 'bg-rank-4 text-white',
  C: 'bg-rank-2 text-white',
  D: 'bg-rank-1 text-white',
} as const;

const VERDICT_LABEL = {
  A: 'A判定 優良',
  B: 'B判定 標準以上',
  C: 'C判定 改善必要',
  D: 'D判定 危機的',
} as const;

export default function Report() {
  const { id } = useParams();
  const diagnosticId = id ? Number(id) : undefined;
  const { data: factorsData } = useFactors();
  const {
    items: roadmapItems,
    loading: roadmapLoading,
    refresh: refreshRoadmap,
  } = useDiagnosticRoadmap(diagnosticId);
  const [detail, setDetail] = useState<DiagnosticDetail | null>(null);
  const [outputModalOpen, setOutputModalOpen] = useState(false);
  const [exportingOptions, setExportingOptions] = useState<ExportOptions | null>(null);
  const [editTarget, setEditTarget] = useState<RoadmapEditTarget | null>(null);

  const exporting = exportingOptions !== null;

  useEffect(() => {
    if (!id) return;
    api
      .get<DiagnosticDetail>(`/api/diagnostics/${id}`)
      .then(setDetail)
      .catch((e) => alert(e instanceof Error ? e.message : '読み込みに失敗しました'));
  }, [id]);

  const factors = factorsData?.factors ?? [];
  const summary = useMemo(() => {
    if (!detail || factors.length === 0) return null;
    const scores: FactorScoreInput[] = detail.scores.map((s) => ({
      factor_id: s.factor_id,
      level: s.level,
    }));
    return summarize(factors, scores);
  }, [detail, factors]);

  if (!detail || !factorsData || !summary) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="mx-auto max-w-6xl px-4 py-8 text-slate-400">読み込み中...</main>
      </div>
    );
  }

  const scoreMap = new Map(detail.scores.map((s) => [s.factor_id, s]));
  const factorsByLevel = (lv: 1 | 2 | 4) =>
    factors.filter((f) => {
      const s = scoreMap.get(f.id);
      if (!s || s.level == null) return false;
      if (lv === 4) return s.level === 4 || s.level === 5;
      return s.level === lv;
    });

  const lv1Factors = factorsByLevel(1);
  const lv2Factors = factorsByLevel(2);
  const strongFactors = factorsByLevel(4);

  const radarData = factorsData.layers.map((l) => ({
    layer: `${l.id} ${l.name}`,
    score: summary.byLayer[l.id] ?? 0,
    fullMark: 5,
  }));

  const layerBarData = factorsData.layers.map((l) => ({
    layer: `${l.id} ${l.name}`,
    score: summary.byLayer[l.id] ?? 0,
  }));

  const categoryBarData = Object.entries(summary.byCategory).map(([key, v]) => ({
    category: `${key} ${v.name}`,
    score: v.score ?? 0,
  }));

  const openEditModal = (item: RoadmapItem) => {
    setEditTarget({
      factor_id: item.factor_id,
      factor_name: item.factor_name,
      layer_name: item.layer_name,
      category_name: item.category_name,
      level: item.level,
      phase: item.phase,
      current_action: item.action,
      isCustomized: item.source === 'diagnostic_override',
      revertLabel: 'カスタマイズを削除',
    });
  };

  const handleEditSave = async (newAction: string) => {
    if (!editTarget || diagnosticId === undefined) return;
    await api.put(
      `/api/diagnostics/${diagnosticId}/roadmap/${editTarget.factor_id}`,
      { custom_action: newAction },
    );
    await refreshRoadmap();
  };

  const handleEditRevert = async () => {
    if (!editTarget || diagnosticId === undefined) return;
    await api.del(
      `/api/diagnostics/${diagnosticId}/roadmap/${editTarget.factor_id}`,
    );
    await refreshRoadmap();
  };

  const handleExport = async (options: ExportOptions) => {
    setExportingOptions(options);
    setOutputModalOpen(false);

    try {
      const filenameBase = `Structive採用力診断_${detail.client.name}_${detail.diagnostic.diagnostic_date}`;
      const exportData = {
        clientName: detail.client.name,
        diagnosticDate: detail.diagnostic.diagnostic_date,
        summary,
        factorsData,
        scoreMap: new Map(
          detail.scores.map((s) => [s.factor_id, { level: s.level, memo: s.memo }]),
        ),
        lv1Factors,
        lv2Factors,
        strongFactors,
        roadmapItems,
        includeRoadmap: options.includeRoadmap,
      };

      if (options.format === 'pptx') {
        await exportReportToPptx(exportData, { filename: filenameBase });
      } else {
        // PDF generated by @react-pdf/renderer — no DOM capture, fully programmatic.
        await exportReportToPdf(exportData, { filename: filenameBase });
      }
    } catch (e) {
      console.error(e);
      alert(e instanceof Error ? e.message : 'レポート出力に失敗しました');
    } finally {
      setExportingOptions(null);
    }
  };

  return (
    <div className="min-h-screen pb-16">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-6" id="report-content">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{detail.client.name}</h1>
            <p className="text-sm text-slate-500">診断日: {detail.diagnostic.diagnostic_date}</p>
          </div>
          <div className="flex gap-2 no-print">
            <button
              onClick={() => setOutputModalOpen(true)}
              disabled={exporting}
              className="btn-secondary"
            >
              {exporting ? '出力中...' : 'レポート出力'}
            </button>
            <Link to={`/diagnostics/${id}`} className="btn-secondary">
              診断に戻る
            </Link>
            <Link to="/" className="btn-primary">
              ダッシュボード
            </Link>
          </div>
        </div>

        {/* Section 1: Overall */}
        <section className="card mb-6">
          <h2 className="mb-4 text-lg font-semibold text-slate-800">総合評価</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div>
              <p className="text-xs text-slate-500">総合スコア</p>
              <p className="font-num text-5xl font-bold text-brand">
                {summary.total !== null ? summary.total.toFixed(1) : '-'}
              </p>
              <p className="text-xs text-slate-400">
                ({summary.filledCount}/{summary.totalCount} 因子入力済)
              </p>
            </div>
            <div>
              <p className="mb-1 text-xs text-slate-500">判定</p>
              {summary.verdict ? (
                <span
                  className={`inline-block rounded-md px-3 py-1.5 text-sm font-bold ${VERDICT_COLOR[summary.verdict]}`}
                >
                  {VERDICT_LABEL[summary.verdict]}
                </span>
              ) : (
                <span className="text-sm text-slate-400">入力データなし</span>
              )}
            </div>
            <div>
              <p className="mb-2 text-xs text-slate-500">レベル分布</p>
              <div className="space-y-1 text-xs">
                <DistRow color="bg-rank-1" label="Lv.1 危機" count={summary.distribution.lv1} />
                <DistRow color="bg-rank-2" label="Lv.2 要改善" count={summary.distribution.lv2} />
                <DistRow color="bg-rank-3" label="Lv.3 標準" count={summary.distribution.lv3} />
                <DistRow color="bg-rank-5" label="Lv.4-5 良好以上" count={summary.distribution.lv4_5} />
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Layer scores */}
        <section className="card mb-6">
          <h2 className="mb-4 text-lg font-semibold text-slate-800">レイヤー別スコア</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="layer" tick={{ fontSize: 11 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 5]} tickCount={6} />
                  <Radar
                    dataKey="score"
                    stroke="#1A3A6E"
                    fill="#378ADD"
                    fillOpacity={0.5}
                  />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={layerBarData} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 5]} />
                  <YAxis dataKey="layer" type="category" tick={{ fontSize: 11 }} width={120} />
                  <Tooltip />
                  <Bar dataKey="score" fill="#378ADD">
                    <LabelList dataKey="score" position="right" formatter={(v: number) => v.toFixed(1)} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        {/* Section 3: Lv.1 crisis */}
        {lv1Factors.length > 0 && (
          <section className="mb-6 rounded-lg border-2 border-rank-1 bg-red-50 p-6">
            <h2 className="mb-3 text-lg font-bold text-rank-1">
              Lv.1 危機項目 — 優先対応({lv1Factors.length}件)
            </h2>
            <div className="space-y-2">
              {lv1Factors.map((f) => (
                <FactorListItem key={f.id} factor={f} memo={scoreMap.get(f.id)?.memo} />
              ))}
            </div>
          </section>
        )}

        {/* Section 4: Lv.2 needs improvement */}
        {lv2Factors.length > 0 && (
          <section className="mb-6 rounded-lg border-2 border-rank-2 bg-orange-50 p-6">
            <h2 className="mb-3 text-lg font-bold text-rank-2">
              Lv.2 要改善項目({lv2Factors.length}件)
            </h2>
            <div className="space-y-2">
              {lv2Factors.map((f) => (
                <FactorListItem key={f.id} factor={f} memo={scoreMap.get(f.id)?.memo} />
              ))}
            </div>
          </section>
        )}

        {/* Section 5: Lv.4-5 strengths */}
        {strongFactors.length > 0 && (
          <section className="mb-6 rounded-lg border-2 border-rank-5 bg-emerald-50 p-6">
            <h2 className="mb-3 text-lg font-bold text-rank-5">
              Lv.4-5 強み(訴求素材)({strongFactors.length}件)
            </h2>
            <div className="space-y-2">
              {strongFactors.map((f) => (
                <FactorListItem
                  key={f.id}
                  factor={f}
                  memo={scoreMap.get(f.id)?.memo}
                  level={scoreMap.get(f.id)?.level ?? undefined}
                />
              ))}
            </div>
          </section>
        )}

        {/* Section 6: Category-level breakdown */}
        <section className="card mb-6">
          <h2 className="mb-4 text-lg font-semibold text-slate-800">中分類別の詳細スコア</h2>
          <div style={{ height: Math.max(280, categoryBarData.length * 32) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={categoryBarData} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 5]} />
                <YAxis dataKey="category" type="category" tick={{ fontSize: 10 }} width={180} />
                <Tooltip />
                <Bar dataKey="score" fill="#1A3A6E">
                  <LabelList dataKey="score" position="right" formatter={(v: number) => v.toFixed(1)} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Section 7: Improvement roadmap */}
        <RoadmapSection
          items={roadmapItems}
          loading={roadmapLoading}
          onEdit={exporting ? undefined : openEditModal}
        />
      </main>

      <OutputModal
        open={outputModalOpen}
        onClose={() => setOutputModalOpen(false)}
        onExport={handleExport}
        exporting={exporting}
      />

      <RoadmapEditModal
        target={editTarget}
        onClose={() => setEditTarget(null)}
        onSave={handleEditSave}
        onRevert={handleEditRevert}
      />
    </div>
  );
}

function DistRow({ color, label, count }: { color: string; label: string; count: number }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`inline-block h-3 w-3 rounded ${color}`} />
      <span className="flex-1 text-slate-600">{label}</span>
      <span className="font-num font-semibold text-slate-800">{count}</span>
    </div>
  );
}

function FactorListItem({
  factor,
  memo,
  level,
}: {
  factor: Factor;
  memo: string | null | undefined;
  level?: number;
}) {
  const currentLevel = (level ?? 1) as 1 | 2 | 3 | 4 | 5;
  const lv = factor.levels[currentLevel];
  return (
    <div className="rounded-md bg-white p-3 shadow-sm">
      <div className="mb-1 flex items-center gap-2">
        <span className="text-xs text-slate-400">#{factor.id}</span>
        <span className="font-medium text-slate-800">{factor.name}</span>
      </div>
      {lv && (
        <p className="mb-1 text-xs text-slate-600">
          <span className="font-medium">現状:</span> {lv.criteria}
        </p>
      )}
      {lv && (
        <p className="text-xs text-slate-500">
          <span className="font-medium">必要なアクション(根拠):</span> {lv.rationale}
        </p>
      )}
      {memo && (
        <p className="mt-1 text-xs italic text-slate-500">メモ: {memo}</p>
      )}
    </div>
  );
}
